import { LinearGradient } from "expo-linear-gradient";
import { useEffect, useRef } from "react";
import {
    Animated,
    StyleSheet,
    View,
    TouchableOpacity,
    Image,
    Modal,
    ScrollView,
    ActivityIndicator,
    Platform
} from "react-native";
import { Text, Icon } from "react-native-paper";

import type { StagedImage } from "@/hooks/useUploadFlow";
import { logRocketRedactProps } from "@/lib/logrocket";
import { captureSentryException } from "@/lib/sentry-init";
import { colors, gradients, radius, shadows, spacing, fonts, typography } from "@/lib/theme";

interface UploadStagingModalProps {
    visible: boolean;
    stagedImages: StagedImage[];
    onAddMoreCamera: () => void;
    onAddMoreGallery: () => void;
    /** Optional — when provided, renders a third "Add PDF" button. */
    onAddMorePdf?: () => void;
    onRemove: (id: string) => void;
    onUpload: () => void;
    onCancel: () => void;
    uploading: boolean;
    uploadProgress: string;
    /** Set after a failed upload attempt (onboarding path). `stagedImages` are
     *  still intact at this point — the message tells the RN to just retry. */
    uploadError?: string | null;
    /**
     * Real 0..1 batch completion, counting bytes of the file in flight. Optional:
     * without it the cloud view keeps its indeterminate spinner, which is what
     * the onboarding path still gets.
     */
    uploadFraction?: number | null;
    /**
     * Upload-in-progress presentation. "spinner" (default, onboarding) shows the
     * small inline spinner row. "cloud" (medications) keeps the sheet open with a
     * cloud-upload animation, progress bar, and a "don't leave this screen"
     * warning — the upload phase is the fragile window where backgrounding the
     * app kills the in-flight PUT, so we keep the RN here and watching.
     */
    uploadingView?: "spinner" | "cloud";
}

export function UploadStagingModal({
    visible,
    stagedImages,
    onAddMoreCamera,
    onAddMoreGallery,
    onAddMorePdf,
    onRemove,
    onUpload,
    onCancel,
    uploading,
    uploadProgress,
    uploadFraction = null,
    uploadingView = "spinner",
    uploadError = null
}: UploadStagingModalProps) {
    const count = stagedImages.length;

    // Guard against unhandled rejections from `onUpload` — `<TouchableOpacity onPress={...}>`
    // discards the returned promise, so anything thrown inside an async `onUpload`
    // (e.g. a failed S3 PUT) would surface as a crash. Report and swallow here;
    // the upstream hook owns the user-facing failure state.
    const handleUploadPress = () => {
        try {
            const maybePromise = onUpload() as unknown;
            if (maybePromise instanceof Promise) {
                maybePromise.catch((err) => captureSentryException(err));
            }
        } catch (err) {
            captureSentryException(err);
        }
    };

    return (
        <Modal visible={visible} transparent animationType="slide">
            <View style={styles.overlay}>
                <View style={styles.sheet}>
                    {/* Header */}
                    <View style={styles.header}>
                        <Text style={styles.headerTitle}>
                            {count} {count === 1 ? "Document" : "Documents"} Selected
                        </Text>
                        {!uploading && (
                            <TouchableOpacity onPress={onCancel} hitSlop={12}>
                                <Icon source="close" size={24} color={colors.textSecondary} />
                            </TouchableOpacity>
                        )}
                    </View>

                    {/* Image grid — hidden during the cloud upload animation so
                        the "uploading to cloud / don't leave" view is the focus. */}
                    {!(uploading && uploadingView === "cloud") && (
                        <ScrollView
                            style={styles.scrollArea}
                            contentContainerStyle={styles.grid}
                            showsVerticalScrollIndicator={false}
                        >
                            {stagedImages.map((img) => (
                                <View key={img.id} style={styles.thumbWrap}>
                                    {/*
                                     * PDFs render as an icon tile with the filename
                                     * — <Image> can't display PDFs, and showing the
                                     * blob URI would result in a broken thumbnail.
                                     */}
                                    {img.kind === "pdf" ? (
                                        <View style={[styles.thumb, styles.pdfThumb]}>
                                            <Icon
                                                source="file-pdf-box"
                                                size={36}
                                                color={colors.teal600}
                                            />
                                            <Text style={styles.pdfThumbLabel} numberOfLines={2}>
                                                {img.filename ?? "PDF"}
                                            </Text>
                                        </View>
                                    ) : (
                                        <Image
                                            {...logRocketRedactProps()}
                                            source={{ uri: img.uri }}
                                            style={styles.thumb}
                                        />
                                    )}
                                    {!uploading && (
                                        <TouchableOpacity
                                            style={styles.removeBtn}
                                            onPress={() => onRemove(img.id)}
                                            hitSlop={8}
                                        >
                                            <Icon
                                                source="close-circle"
                                                size={22}
                                                color={colors.error500}
                                            />
                                        </TouchableOpacity>
                                    )}
                                </View>
                            ))}
                        </ScrollView>
                    )}

                    {/* Actions */}
                    {uploading ? (
                        uploadingView === "cloud" ? (
                            <CloudUploadView progress={uploadProgress} fraction={uploadFraction} />
                        ) : (
                            <View style={styles.progressRow}>
                                <ActivityIndicator size="small" color={colors.teal400} />
                                <Text style={styles.progressText}>{uploadProgress}</Text>
                            </View>
                        )
                    ) : (
                        <>
                            {uploadError && (
                                <View style={styles.uploadErrorBox}>
                                    <Icon
                                        source="alert-circle-outline"
                                        size={18}
                                        color={colors.error500}
                                    />
                                    <Text style={styles.uploadErrorText}>{uploadError}</Text>
                                </View>
                            )}

                            {/* Add more buttons */}
                            <View style={styles.addMoreRow}>
                                <TouchableOpacity
                                    style={styles.addMoreBtn}
                                    onPress={onAddMoreCamera}
                                >
                                    <Icon source="camera" size={18} color={colors.teal600} />
                                    <Text style={styles.addMoreText}>Camera</Text>
                                </TouchableOpacity>
                                <TouchableOpacity
                                    style={styles.addMoreBtn}
                                    onPress={onAddMoreGallery}
                                >
                                    <Icon source="image-outline" size={18} color={colors.teal600} />
                                    <Text style={styles.addMoreText}>Gallery</Text>
                                </TouchableOpacity>
                                {/*
                                 * PDF option only renders when the parent has
                                 * opted in via `onAddMorePdf`. Callers like the
                                 * medication-extraction upload stay image-only
                                 * by simply omitting the prop.
                                 */}
                                {onAddMorePdf && (
                                    <TouchableOpacity
                                        style={styles.addMoreBtn}
                                        onPress={onAddMorePdf}
                                    >
                                        <Icon
                                            source="file-pdf-box"
                                            size={18}
                                            color={colors.teal600}
                                        />
                                        <Text style={styles.addMoreText}>PDF</Text>
                                    </TouchableOpacity>
                                )}
                            </View>

                            {/* Upload button */}
                            <View style={styles.uploadBtnWrap}>
                                <TouchableOpacity onPress={handleUploadPress} activeOpacity={0.8}>
                                    <LinearGradient
                                        colors={[...gradients.teal]}
                                        start={{ x: 0, y: 0 }}
                                        end={{ x: 1, y: 0 }}
                                        style={[styles.uploadBtn, shadows.brand]}
                                    >
                                        <Icon
                                            source="cloud-upload"
                                            size={20}
                                            color={colors.surface}
                                        />
                                        <Text style={styles.uploadBtnText}>
                                            Upload {count} {count === 1 ? "Document" : "Documents"}
                                        </Text>
                                    </LinearGradient>
                                </TouchableOpacity>
                            </View>

                            {/* Cancel */}
                            <TouchableOpacity style={styles.cancelBtn} onPress={onCancel}>
                                <Text style={styles.cancelText}>Cancel</Text>
                            </TouchableOpacity>
                        </>
                    )}
                </View>
            </View>
        </Modal>
    );
}

/**
 * Medications upload-in-progress view. Keeps the RN on the sheet (app
 * foregrounded) while images stream to S3 — the fragile window where locking the
 * phone or switching apps kills the in-flight PUT. A bobbing cloud + progress bar
 * + an explicit "don't leave" warning make that window visible.
 */
function CloudUploadView({ progress, fraction }: { progress: string; fraction: number | null }) {
    const bob = useRef(new Animated.Value(0)).current;
    const pulse = useRef(new Animated.Value(0.85)).current;
    const useNative = Platform.OS !== "web";

    useEffect(() => {
        const b = Animated.loop(
            Animated.sequence([
                Animated.timing(bob, { toValue: -8, duration: 900, useNativeDriver: useNative }),
                Animated.timing(bob, { toValue: 0, duration: 900, useNativeDriver: useNative })
            ])
        );
        const p = Animated.loop(
            Animated.sequence([
                Animated.timing(pulse, {
                    toValue: 1.1,
                    duration: 1100,
                    useNativeDriver: useNative
                }),
                Animated.timing(pulse, {
                    toValue: 0.85,
                    duration: 1100,
                    useNativeDriver: useNative
                })
            ])
        );
        b.start();
        p.start();
        return () => {
            b.stop();
            p.stop();
        };
    }, [bob, pulse, useNative]);

    return (
        <View style={styles.cloudWrap}>
            <View style={styles.cloudAnimArea}>
                <Animated.View style={[styles.cloudGlow, { transform: [{ scale: pulse }] }]} />
                <Animated.View style={{ transform: [{ translateY: bob }] }}>
                    <Icon source="cloud-upload" size={64} color={colors.teal400} />
                </Animated.View>
            </View>
            <Text style={styles.cloudTitle}>Uploading meds to cloud…</Text>
            <ActivityIndicator size="small" color={colors.teal400} style={{ marginTop: 4 }} />
            <Text style={styles.cloudProgress}>{progress}</Text>
            {/* Determinate bar driven by real bytes sent. On a large batch over a
                weak signal the "N of M" line can sit still for a long time on one
                photo; the bar keeps moving so a slow upload doesn't read as a
                frozen one. */}
            {fraction !== null && (
                <View style={styles.barTrack}>
                    <View
                        style={[
                            styles.barFill,
                            { width: `${Math.round(Math.min(1, Math.max(0, fraction)) * 100)}%` }
                        ]}
                    />
                </View>
            )}
            <View style={styles.dontLeaveBox}>
                <Icon source="alert-circle-outline" size={18} color={colors.warning600} />
                <Text style={styles.dontLeaveText}>
                    Please keep this screen open — don&apos;t lock your phone or switch apps until
                    upload finishes.
                </Text>
            </View>
        </View>
    );
}

const THUMB_SIZE = 100;

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.5)",
        justifyContent: "flex-end"
    },
    sheet: {
        backgroundColor: colors.surface,
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        maxHeight: "70%",
        paddingBottom: 34
    },
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        paddingHorizontal: spacing.base,
        paddingVertical: spacing.md,
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    headerTitle: {
        fontSize: 18,
        fontFamily: fonts.bold,
        color: colors.textPrimary
    },
    scrollArea: {
        maxHeight: 280,
        paddingHorizontal: spacing.base
    },
    grid: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: spacing.sm,
        paddingVertical: spacing.md
    },
    thumbWrap: {
        position: "relative"
    },
    thumb: {
        width: THUMB_SIZE,
        height: THUMB_SIZE,
        borderRadius: radius.md,
        backgroundColor: colors.backgroundSecondary
    },
    pdfThumb: {
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        padding: 6,
        borderWidth: 1,
        borderColor: colors.teal100
    },
    pdfThumbLabel: {
        ...typography.caption,
        color: colors.textSecondary,
        textAlign: "center"
    },
    removeBtn: {
        position: "absolute",
        top: -6,
        right: -6,
        backgroundColor: colors.surface,
        borderRadius: 11
    },
    progressRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.sm,
        paddingVertical: spacing.lg
    },
    cloudWrap: {
        alignItems: "center",
        paddingHorizontal: spacing.lg,
        paddingTop: spacing.lg,
        paddingBottom: spacing.sm
    },
    cloudAnimArea: {
        width: 110,
        height: 96,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: spacing.sm
    },
    cloudGlow: {
        position: "absolute",
        width: 96,
        height: 96,
        borderRadius: 48,
        backgroundColor: "rgba(69,210,176,0.10)"
    },
    cloudTitle: {
        fontSize: 18,
        fontFamily: fonts.bold,
        color: colors.textPrimary,
        textAlign: "center"
    },
    cloudProgress: {
        fontSize: 13,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        marginTop: spacing.sm,
        marginBottom: spacing.md
    },
    barTrack: {
        width: "78%",
        height: 6,
        borderRadius: radius.full,
        backgroundColor: colors.border,
        overflow: "hidden",
        marginBottom: spacing.md
    },
    barFill: {
        height: "100%",
        borderRadius: radius.full,
        backgroundColor: colors.teal400
    },
    dontLeaveBox: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        backgroundColor: colors.warning50,
        borderWidth: 1,
        borderColor: "#FDE68A",
        borderRadius: radius.md,
        paddingVertical: spacing.md,
        paddingHorizontal: spacing.base
    },
    dontLeaveText: {
        flex: 1,
        fontSize: 13,
        fontFamily: fonts.semiBold,
        color: colors.warning600,
        lineHeight: 18
    },
    progressText: {
        ...typography.body,
        color: colors.textSecondary
    },
    uploadErrorBox: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        backgroundColor: colors.errorLight,
        borderWidth: 1,
        borderColor: colors.error500,
        borderRadius: radius.md,
        paddingVertical: spacing.md,
        paddingHorizontal: spacing.base,
        marginHorizontal: spacing.base,
        marginBottom: spacing.md
    },
    uploadErrorText: {
        flex: 1,
        fontSize: 13,
        fontFamily: fonts.semiBold,
        color: colors.error600,
        lineHeight: 18
    },
    addMoreRow: {
        flexDirection: "row",
        gap: spacing.sm,
        paddingHorizontal: spacing.base,
        marginBottom: spacing.md
    },
    addMoreBtn: {
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.xs,
        paddingVertical: 10,
        borderRadius: radius.md,
        borderWidth: 1.5,
        borderColor: colors.teal400,
        backgroundColor: colors.teal50
    },
    addMoreText: {
        fontSize: 13,
        fontFamily: fonts.semiBold,
        color: colors.teal600
    },
    uploadBtnWrap: {
        paddingHorizontal: spacing.base,
        marginBottom: spacing.sm
    },
    uploadBtn: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.sm,
        borderRadius: radius.full,
        paddingVertical: 14
    },
    uploadBtnText: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: colors.surface
    },
    cancelBtn: {
        alignItems: "center",
        paddingVertical: spacing.sm
    },
    cancelText: {
        ...typography.body,
        color: colors.textSecondary
    }
});
